import React, { useCallback, useEffect, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  TenantProviderConfig,
  PROVIDER_LABELS,
  ProviderName,
} from "../../model/SitePayment";
import PaymentProviderForm from "./PaymentProviderForm";
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react";

const PaymentProviderList: React.FC = () => {
  const [providers, setProviders] = useState<TenantProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProvider, setEditingProvider] =
    useState<TenantProviderConfig | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(API_URLS.PAYMENT_PROVIDER_CONFIGS);
      setProviders(res.data?.data || res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar fornecedores.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleToggleEnabled = async (provider: TenantProviderConfig) => {
    try {
      await api.put(API_URLS.PAYMENT_PROVIDER_CONFIGS, {
        provider_name: provider.provider_name,
        is_enabled: !provider.is_enabled,
        environment: provider.environment,
      });
      await fetchProviders();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao atualizar estado.");
    }
  };

  const handleDelete = async (provider: TenantProviderConfig) => {
    if (
      !confirm(
        `Eliminar fornecedor "${PROVIDER_LABELS[provider.provider_name] || provider.provider_name}"?`,
      )
    )
      return;
    try {
      await api.delete(
        `${API_URLS.PAYMENT_PROVIDER_CONFIGS}/${provider.provider_name}`,
      );
      await fetchProviders();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao eliminar.");
    }
  };

  const handleFormClose = (saved: boolean) => {
    setShowForm(false);
    setEditingProvider(null);
    if (saved) fetchProviders();
  };

  if (loading) return <div className="text-gray-500 py-4">A carregar...</div>;

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Fornecedores de Pagamento
        </h2>
        <button
          className="button is-primary is-small flex items-center gap-1"
          onClick={() => {
            setEditingProvider(null);
            setShowForm(true);
          }}
        >
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      {providers.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhum fornecedor configurado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 px-3">Fornecedor</th>
                <th className="py-2 px-3">Ambiente</th>
                <th className="py-2 px-3">API Key</th>
                <th className="py-2 px-3">Estado</th>
                <th className="py-2 px-3 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium">
                    {PROVIDER_LABELS[p.provider_name] || p.provider_name}
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${p.environment === "live" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                    >
                      {p.environment}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-500 font-mono text-xs">
                    {p.api_key_masked || "-"}
                  </td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => handleToggleEnabled(p)}
                      className="flex items-center gap-1"
                      title={p.is_enabled ? "Desativar" : "Ativar"}
                    >
                      {p.is_enabled ? (
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      )}
                      <span
                        className={
                          p.is_enabled ? "text-green-600" : "text-gray-400"
                        }
                      >
                        {p.is_enabled ? "Ativo" : "Inativo"}
                      </span>
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        title="Editar"
                        onClick={() => {
                          setEditingProvider(p);
                          setShowForm(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="text-red-500 hover:text-red-700"
                        title="Eliminar"
                        onClick={() => handleDelete(p)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <PaymentProviderForm
          provider={editingProvider}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default PaymentProviderList;
